import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, Car, MapPin, Clock, Users, DollarSign, 
  Loader2, Navigation, Fuel, AlertCircle, CheckCircle2, MapPinned,
  Calendar, Hash, StickyNote
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { AddressLink } from '../components/common/AddressLink';
import { ScreenHelp } from '../components/common/ScreenHelp';
import { MobileFormModal, MobileFormFooter } from '../components/common/MobileFormModal';
import { LabelWithTooltip, FIELD_TIPS } from '../components/common/FieldTooltip';
import { PageHeader } from '../components/common/PageHeader';

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '12px',
};

const defaultCenter = {
  lat: -23.5505,
  lng: -46.6333,
};

export default function TripCarpools() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { trip } = context || {};
  const { api, user } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'en-US' ? enUS : ptBR;
  
  const [carpools, setCarpools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCarpool, setSelectedCarpool] = useState(null);
  const [directions, setDirections] = useState(null);
  
  const [formData, setFormData] = useState({
    departure_location: '',
    destination: '',
    departure_datetime: '',
    available_seats: 4,
    notes: '',
    estimated_cost: '',
    fuel_cost: '',
    toll_cost: '',
    meeting_point: '',
    meeting_time: '',
  });

  // Detect mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    fetchCarpools();
  }, [tripId]);

  const fetchCarpools = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/carpools`);
      setCarpools(response.data);
    } catch (error) {
      console.error('Error fetching carpools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (!formData.departure_location || !formData.destination || !formData.departure_datetime) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    try {
      await api.post(`/trips/${tripId}/carpools`, {
        ...formData,
        trip_id: tripId,
        departure_datetime: new Date(formData.departure_datetime).toISOString(),
        available_seats: parseInt(formData.available_seats),
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        fuel_cost: formData.fuel_cost ? parseFloat(formData.fuel_cost) : null,
        toll_cost: formData.toll_cost ? parseFloat(formData.toll_cost) : null,
        meeting_point: formData.meeting_point || null,
        meeting_time: formData.meeting_time || null,
      });
      toast.success('Carona oferecida!');
      setDialogOpen(false);
      setFormData({
        departure_location: '',
        destination: '',
        departure_datetime: '',
        available_seats: 4,
        notes: '',
        estimated_cost: '',
        fuel_cost: '',
        toll_cost: '',
        meeting_point: '',
        meeting_time: '',
      });
      fetchCarpools();
    } catch (error) {
      toast.error('Erro ao criar carona');
    }
  };

  const handleRequestSeat = async (carpoolId) => {
    try {
      await api.post(`/trips/${tripId}/carpools/${carpoolId}/request`);
      toast.success('Solicitação enviada!');
      fetchCarpools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar vaga');
    }
  };

  const handleAcceptPassenger = async (carpoolId, passengerId) => {
    try {
      await api.post(`/trips/${tripId}/carpools/${carpoolId}/accept/${passengerId}`);
      toast.success('Passageiro aceito!');
      fetchCarpools();
    } catch (error) {
      toast.error('Erro ao aceitar passageiro');
    }
  };

  const calculateRoute = useCallback(async (origin, dest) => {
    if (!isLoaded || !window.google) return;
    
    const directionsService = new window.google.maps.DirectionsService();
    try {
      const result = await directionsService.route({
        origin,
        destination: dest,
        travelMode: window.google.maps.TravelMode.DRIVING,
      });
      setDirections(result);
    } catch (error) {
      console.error('Error calculating route:', error);
    }
  }, [isLoaded]);

  const viewRoute = (carpool) => {
    setSelectedCarpool(carpool);
    if (carpool.departure_location && carpool.destination) {
      calculateRoute(carpool.departure_location, carpool.destination);
    }
  };

  const statusConfig = {
    open: { label: 'Vagas disponíveis', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
    full: { label: 'Lotado', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
    departed: { label: 'Partiu', color: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300' },
    cancelled: { label: 'Cancelado', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Caronas"
        subtitle="Organize o transporte e divida custos"
        helpScreen="carpools"
        actions={
          <>
            {/* Mobile Modal */}
            {isMobile ? (
              <Button 
                className="rounded-full bg-orange-500 hover:bg-orange-600" 
                onClick={() => setDialogOpen(true)}
                data-testid="offer-carpool-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Oferecer
              </Button>
            ) : (
              /* Desktop Modal Trigger */
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-orange-500 hover:bg-orange-600" data-testid="offer-carpool-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Oferecer carona
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Oferecer carona</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div>
                      <LabelWithTooltip tip={FIELD_TIPS.carpools.departure} required>
                        Local de partida
                      </LabelWithTooltip>
                      <div className="relative mt-1">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={formData.departure_location}
                          onChange={(e) => setFormData({ ...formData, departure_location: e.target.value })}
                          placeholder="Ex: Av. Paulista, 1000, São Paulo"
                          className="pl-10"
                          data-testid="carpool-departure"
                        />
                      </div>
                    </div>
                    <div>
                      <LabelWithTooltip tip={FIELD_TIPS.carpools.destination} required>
                        Destino
                      </LabelWithTooltip>
                      <div className="relative mt-1">
                        <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={formData.destination}
                          onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                          placeholder={trip?.destination || 'Destino da viagem'}
                          className="pl-10"
                          data-testid="carpool-destination"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <LabelWithTooltip tip={FIELD_TIPS.carpools.departureTime} required>
                          Data e hora
                        </LabelWithTooltip>
                        <Input
                          type="datetime-local"
                          value={formData.departure_datetime}
                          onChange={(e) => setFormData({ ...formData, departure_datetime: e.target.value })}
                          className="mt-1"
                          data-testid="carpool-datetime"
                        />
                      </div>
                      <div>
                        <LabelWithTooltip tip={FIELD_TIPS.carpools.seats}>
                          Vagas
                        </LabelWithTooltip>
                        <Input
                          type="number"
                          value={formData.available_seats}
                          onChange={(e) => setFormData({ ...formData, available_seats: e.target.value })}
                          className="mt-1"
                          min={1}
                          max={7}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <LabelWithTooltip tip={FIELD_TIPS.carpools.fuelCost}>
                          Combustível (R$)
                        </LabelWithTooltip>
                        <div className="relative mt-1">
                          <Fuel className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            type="number"
                            value={formData.fuel_cost}
                            onChange={(e) => setFormData({ ...formData, fuel_cost: e.target.value })}
                            placeholder="0,00"
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Pedágios (R$)</Label>
                        <div className="relative mt-1">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            type="number"
                            value={formData.toll_cost}
                            onChange={(e) => setFormData({ ...formData, toll_cost: e.target.value })}
                            placeholder="0,00"
                            className="pl-10"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <LabelWithTooltip tip={FIELD_TIPS.carpools.notes}>
                        Observações
                      </LabelWithTooltip>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Ponto de encontro, bagagem, etc..."
                        className="mt-1"
                      />
                    </div>

                    {/* Meeting Point Section */}
                    <div className="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                      <LabelWithTooltip tip={FIELD_TIPS.carpools.meetingPoint} tipType="tip">
                        <MapPinned className="w-4 h-4 inline mr-1" />
                        Ponto de Encontro (opcional)
                      </LabelWithTooltip>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Local do encontro</Label>
                        <Input
                          value={formData.meeting_point}
                          onChange={(e) => setFormData({ ...formData, meeting_point: e.target.value })}
                          placeholder="Ex: Em frente ao shopping, próximo ao metrô..."
                          className="mt-1"
                          data-testid="carpool-meeting-point"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Horário do encontro</Label>
                        <Input
                          type="time"
                          value={formData.meeting_time}
                          onChange={(e) => setFormData({ ...formData, meeting_time: e.target.value })}
                          className="mt-1"
                          data-testid="carpool-meeting-time"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Defina um horário para encontrar os passageiros antes da partida
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600">
                        Oferecer
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </>
        }
      />

      {/* Mobile Form Modal */}
      {isMobile && (
        <MobileFormModal
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title="Oferecer carona"
                footer={
                  <MobileFormFooter
                    onCancel={() => setDialogOpen(false)}
                    onSubmit={handleCreate}
                    cancelText="Cancelar"
                    submitText="Oferecer"
                  />
                }
              >
                <form onSubmit={handleCreate} className="space-y-2">
                  <div className="py-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Local de partida *</label>
                    <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                      <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <input
                        value={formData.departure_location}
                        onChange={(e) => setFormData({ ...formData, departure_location: e.target.value })}
                        placeholder="Ex: Av. Paulista, 1000"
                        className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                        data-testid="carpool-departure-mobile"
                      />
                    </div>
                  </div>
                  
                  <div className="py-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Destino *</label>
                    <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                      <Navigation className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <input
                        value={formData.destination}
                        onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                        placeholder={trip?.destination || 'Destino da viagem'}
                        className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                        data-testid="carpool-destination-mobile"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="py-2">
                      <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Data/hora *</label>
                      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                        <Calendar className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <input
                          type="datetime-local"
                          value={formData.departure_datetime}
                          onChange={(e) => setFormData({ ...formData, departure_datetime: e.target.value })}
                          className="w-full bg-transparent border-0 focus:ring-0 px-0 py-2 text-base text-slate-900 dark:text-white outline-none"
                        />
                      </div>
                    </div>
                    <div className="py-2">
                      <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Vagas</label>
                      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                        <Users className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <input
                          type="number"
                          value={formData.available_seats}
                          onChange={(e) => setFormData({ ...formData, available_seats: e.target.value })}
                          min={1}
                          max={7}
                          className="w-full bg-transparent border-0 focus:ring-0 px-0 py-2 text-base text-slate-900 dark:text-white outline-none"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="py-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Modelo do carro</label>
                    <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                      <Car className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Ex: Honda Civic preto"
                        className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="py-2">
                      <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Combustível</label>
                      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                        <Fuel className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <input
                          type="number"
                          value={formData.fuel_cost}
                          onChange={(e) => setFormData({ ...formData, fuel_cost: e.target.value })}
                          placeholder="R$ 0,00"
                          className="w-full bg-transparent border-0 focus:ring-0 px-0 py-2 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                        />
                      </div>
                    </div>
                    <div className="py-2">
                      <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Pedágio</label>
                      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                        <DollarSign className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <input
                          type="number"
                          value={formData.toll_cost}
                          onChange={(e) => setFormData({ ...formData, toll_cost: e.target.value })}
                          placeholder="R$ 0,00"
                          className="w-full bg-transparent border-0 focus:ring-0 px-0 py-2 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="py-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Ponto de encontro</label>
                    <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                      <MapPinned className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <input
                        value={formData.meeting_point}
                        onChange={(e) => setFormData({ ...formData, meeting_point: e.target.value })}
                        placeholder="Ex: Em frente ao shopping..."
                        className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                      />
                    </div>
                  </div>
                </form>
              </MobileFormModal>
      )}

      {/* Map Preview */}
      {isLoaded && selectedCarpool && (
        <Card className="border-slate-100 dark:border-slate-700 overflow-hidden">
          <CardContent className="p-0">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={10}
            >
              {directions && <DirectionsRenderer directions={directions} />}
            </GoogleMap>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : carpools.length === 0 ? (
        <Card className="border-slate-100 dark:border-slate-700 border-dashed">
          <CardContent className="py-12 text-center">
            <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              Nenhuma carona disponível
            </h3>
            <p className="text-slate-500 dark:text-slate-400">Seja o primeiro a oferecer uma carona!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {carpools.map((carpool, index) => {
            const status = statusConfig[carpool.status] || statusConfig.open;
            const availableSeats = carpool.available_seats - (carpool.seats_taken || 0);
            const isDriver = carpool.driver_id === user?.id;
            const isPassenger = carpool.passengers?.some(p => p.user_id === user?.id);
            const totalCost = (carpool.fuel_cost || 0) + (carpool.toll_cost || 0);
            const costPerPerson = carpool.cost_per_person || (totalCost / (carpool.seats_taken + 1));

            return (
              <motion.div
                key={carpool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="border-slate-100 dark:border-slate-700 hover:shadow-card-hover transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Driver Avatar */}
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-orange-100 text-orange-600 text-lg">
                          {carpool.driver_avatar || '🚗'}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-900 dark:text-white">{carpool.driver_name}</h3>
                              {isDriver && <Badge variant="outline" className="text-xs">Você</Badge>}
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {format(parseISO(carpool.departure_datetime), "EEEE, dd/MM 'às' HH:mm", { locale: ptBR })}
                            </p>
                          </div>
                          <Badge className={status.color}>{status.label}</Badge>
                        </div>

                        {/* Route */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm">
                              <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                              <AddressLink 
                                address={carpool.departure_location} 
                                className="text-slate-600"
                                showIcon={false}
                              />
                            </div>
                            <div className="w-0.5 h-4 bg-slate-200 ml-1" />
                            <div className="flex items-center gap-2 text-sm">
                              <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                              <AddressLink 
                                address={carpool.destination} 
                                className="text-slate-600"
                                showIcon={false}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Info */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            <span>{availableSeats} vagas livres</span>
                          </div>
                          {totalCost > 0 && (
                            <div className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              <span>~R$ {costPerPerson.toFixed(2)}/pessoa</span>
                            </div>
                          )}
                        </div>

                        {carpool.notes && (
                          <p className="text-sm text-slate-500 mt-2 italic">📝 {carpool.notes}</p>
                        )}

                        {/* Meeting Point */}
                        {(carpool.meeting_point || carpool.meeting_time) && (
                          <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/10 rounded-lg border border-orange-200 dark:border-orange-800">
                            <p className="text-xs font-medium text-orange-700 dark:text-orange-400 flex items-center gap-1 mb-1">
                              <MapPinned className="w-3 h-3" />
                              Ponto de Encontro
                            </p>
                            {carpool.meeting_point && (
                              <AddressLink 
                                address={carpool.meeting_point} 
                                className="text-sm text-slate-700 dark:text-slate-300"
                                showIcon={false}
                              />
                            )}
                            {carpool.meeting_time && (
                              <p className="text-xs text-slate-500 mt-1">
                                <Clock className="w-3 h-3 inline mr-1" />
                                Encontro às {carpool.meeting_time}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Passengers */}
                        {carpool.passengers && carpool.passengers.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Passageiros:</p>
                            <div className="flex flex-wrap gap-2">
                              {carpool.passengers.map((p) => (
                                <div key={p.id} className="flex items-center gap-1 bg-slate-50 rounded-full px-2 py-1">
                                  <span className="text-sm">{p.user_avatar || '👤'}</span>
                                  <span className="text-sm text-slate-600 dark:text-slate-400">{p.user_name}</span>
                                  {p.status === 'pending' && (
                                    <Badge className="bg-yellow-100 text-yellow-700 text-xs">Pendente</Badge>
                                  )}
                                  {isDriver && p.status === 'pending' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-green-600"
                                      onClick={() => handleAcceptPassenger(carpool.id, p.id)}
                                    >
                                      <CheckCircle2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                            onClick={() => viewRoute(carpool)}
                          >
                            <MapPin className="w-4 h-4 mr-1" />
                            Ver rota
                          </Button>
                          {!isDriver && !isPassenger && carpool.status === 'open' && availableSeats > 0 && (
                            <Button
                              size="sm"
                              className="rounded-full bg-orange-500 hover:bg-orange-600"
                              onClick={() => handleRequestSeat(carpool.id)}
                              data-testid={`request-seat-${carpool.id}`}
                            >
                              Pedir carona
                            </Button>
                          )}
                          {isPassenger && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Você está nessa carona
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
